import { CourseEventType, CourseKind } from '../../../models/course-kind.model';
import { SessionOnSitePayment } from './on-site-payment';

// Enums matching backend
export enum JoinPolicy {
  OPEN = 'open',
  APPROVAL_REQUIRED = 'approval_required',
  INVITE_ONLY = 'invite_only',
}

export enum Visibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  MEMBERS_ONLY = 'members_only',
}

export enum SessionStatus {
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
}

export enum ParticipantStatus {
  ENROLLED = 'enrolled',
  CANCELLED = 'cancelled',
}

export enum WaitlistStatus {
  WAITING = 'waiting',
  PROMOTED = 'promoted',
  CANCELLED = 'cancelled',
}

// Common DTOs
export interface CourtSummary {
  id: string;
  displayName: string;
}

// Unified court format for sessions (matches AggregatedCourseSessionDto)
export interface SessionCourt {
  courtId: string;
  courtName: string;
}

export interface UserSummary {
  id: string;
  name: string;
  email?: string;
}

// Course interfaces
export interface Course {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  /** 'course' (Kurs or event) | 'block' (Platzsperre). Blocks never reach a member surface. */
  kind: CourseKind;
  /** NULL on a `course` means "Kurs"; a set value makes it an event (Spieltag/Turnier/Sonstiges). */
  eventType: CourseEventType | null;
  waitlistEnabled: boolean;
  /** Whether the course is active (bookable / customer-facing). */
  isActive: boolean;
  maxWaitlistSize?: number | null;
  sessionsCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Session interfaces
export interface CourseSession {
  id: string;
  courseId: string;
  courseName: string;
  /** Per-occurrence headline override; null = inherit the course name. */
  title: string | null;
  /** Server-side coalesce of `title` and `courseName` — always render THIS. */
  displayName: string;
  kind: CourseKind;
  eventType: CourseEventType | null;
  startTime: string;
  endTime: string;
  date: string;
  instructor?: UserSummary;
  maxParticipants: number;
  currentParticipants: number;
  availableSpots: number;
  status: SessionStatus;
  pricePerSession: number;
  joinPolicy: JoinPolicy;
  visibility: Visibility;
  /**
   * The STORED override; `null` = inherits the organization. Admin
   * configuration, NOT a per-user permission — the checkout answer is decided
   * server-side and depends on the paying user's membership.
   */
  onSitePayment: SessionOnSitePayment | null;
  // Unified court format (matches AggregatedCourseSessionDto)
  courts: SessionCourt[];
  // Unified enrollment fields (matches AggregatedCourseSessionDto)
  isFull: boolean;
  isUserEnrolled?: boolean;
  /** Current user's booking id (UUID) when enrolled — links to the booking detail page. */
  userBookingId?: string;
  isUserOnWaitlist?: boolean;
  userWaitlistPosition?: number;
  waitlistEnabled?: boolean;
  cancellationReason?: string;
  canEnroll?: boolean;
  enrollmentMessage?: string;
  enrollmentCutoffMinutes: number;
  isEnrollmentOpen: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * `GET /sessions/:id` — the USER-SCOPED single-session read, and the only
 * session payload that answers a per-user question (backend
 * `SessionDetailResponseDto`).
 *
 * It is a separate interface rather than an optional field on `CourseSession`
 * so the list payloads — which have no paying user server-side — cannot appear
 * to carry the verdict. If you have a `CourseSession`, you do not have the
 * answer; fetch it.
 */
export interface CourseSessionDetail extends CourseSession {
  /**
   * "May THIS user pay THIS session at the venue?" — resolved server-side from
   * the org switch × org audience × the session override, crossed against the
   * caller's membership, by the SAME code the enrolment gate runs. `true` for
   * a free session (the gate does not apply). Fails closed.
   *
   * Read this instead of the org-wide `payment.onSitePaymentEnabled` when
   * deciding whether to offer "Zahlung vor Ort" for a course session — the
   * org-wide flag cannot see the session's override in either direction.
   */
  onSitePaymentAvailable: boolean;
}

// Participant interfaces
export interface Participant {
  id: string;
  userId: string;
  /** Resolved display name. */
  userName: string;
  /** Real first + last name; shown alongside userName in admin views. */
  userFullName?: string | null;
  userEmail?: string;
  status: ParticipantStatus;
  enrolledAt: string;
  canceledAt?: string;
  bookingId?: string;
  bookingBusinessId?: string;
  paymentMethod?: 'stripe' | 'credit_card' | 'on_site' | 'voucher' | null;
  paymentStatus?:
    | 'pending'
    | 'succeeded'
    | 'completed'
    | 'failed'
    | 'refunded'
    | 'partial'
    | null;
  pricePerSession?: number | null;
}

export interface WaitlistEntry {
  id: string;
  userId: string;
  /** Resolved display name. */
  userName: string;
  /** Real first + last name; shown alongside userName in admin views. */
  userFullName?: string | null;
  userEmail?: string;
  position: number;
  status: WaitlistStatus;
  requestedAt: string;
}

export interface SessionParticipants {
  participants: Participant[];
  waitlist: WaitlistEntry[];
  totalParticipants: number;
  totalWaitlist: number;
  maxParticipants: number;
  availableSpots: number;
}

export interface RemoveParticipantRequest {
  participantId: string;
  reason?: string;
}

// Enrollment interfaces
export interface EnrollmentResponse {
  participantId: string;
  sessionId: string;
  bookingId?: string | null;
  bookingBusinessId?: string | null;
  status: 'enrolled' | 'waitlisted';
  waitlistPosition?: number;
  paymentRequired: boolean;
  paymentIntentClientSecret?: string;
}

export interface CancelEnrollmentRequest {
  reason?: string;
}

export interface CancelEnrollmentResponse {
  success: boolean;
  sessionId: string;
  cancellationId: string;
  refundAmount?: number;
  refundVoucherId?: string;
  refundVoucherCode?: string;
  cancellationFee?: number;
  message?: string;
}

export interface CancellationEligibility {
  canCancel: boolean;
  cancellableUntil?: string;
  cancellationFee?: number;
  estimatedRefund?: number;
  dialogMessage?: string;
  message?: string;
}
